import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Menu,
    MenuItem,
    Paper,
    Typography,
} from '@mui/material';
import {
    ArrowDropDown,
    AttachFile,
    Check,
    Inventory2Outlined,
    LocalShippingOutlined,
    ReceiptLongOutlined,
    PictureAsPdfOutlined,
    OpenInNew,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../../../Website/utils/api';
import { getRecievedOrderDetailsThunk, setReceiveOrdersRefresh } from '../../../../Website/redux/receivedVendorOrderSlice';
import { computeGstTotals, formatINR } from '../../../../Website/utils/gstTotals';
import Myprofile from '../myprofile/Myprofile';
import OrderProgress from '../../Components/orderscomp/OrderProgress';

// Statuses the vendor can move an order to (cancel has its own button)
const STATUS_OPTIONS = ['NEW', 'PROCESSING', 'PENDING', 'SHIPPED', 'DELIVERED'];

const STATUS_STYLES = {
    NEW: { label: 'New', color: '#4A4C56', bg: '#F0F1F3' },
    PROCESSING: { label: 'Processing', color: '#F86624', bg: '#FFF0EA' },
    PENDING: { label: 'Pending', color: '#B7791F', bg: '#FFF8E6' },
    SHIPPED: { label: 'Shipped', color: '#2BB2FE', bg: '#EAF8FF' },
    DELIVERED: { label: 'Delivered', color: '#1A9882', bg: '#E9FAF7' },
    CANCELED: { label: 'Cancelled', color: '#F43224', bg: '#FEECEB' },
    CANCELLED: { label: 'Cancelled', color: '#F43224', bg: '#FEECEB' },
};

const cardSx = {
    boxShadow: '0px 4px 30px 0px #4D54640D',
    border: '1px solid #E0E2E7',
    borderRadius: '12px',
};

const formatDate = (date) =>
    date
        ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
        : '-';

const formatQty = (value) => (Number(value) > 0 ? Number(value).toLocaleString('en-IN') : '-');

const StatusChip = ({ status }) => {
    const style = STATUS_STYLES[status] || STATUS_STYLES.NEW;
    return (
        <Chip
            label={style.label}
            size="small"
            sx={{ backgroundColor: style.bg, color: style.color, fontWeight: 600, borderRadius: '6px', fontSize: '13px' }}
        />
    );
};

const CardHeader = ({ title, subtitle, icon: Icon }) => (
    <>
        <Box sx={{ px: '20px', py: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            {Icon && (
                <Box sx={{ width: 32, height: 32, display: 'grid', placeItems: 'center', borderRadius: '9px', color: '#6637C7', background: '#F3EEFF' }}>
                    <Icon sx={{ fontSize: 18 }} />
                </Box>
            )}
            <Box>
                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1D1F2C', lineHeight: 1.25 }}>{title}</Typography>
                {subtitle && <Typography sx={{ mt: '2px', fontSize: '12px', color: '#727A90' }}>{subtitle}</Typography>}
            </Box>
        </Box>
        <Divider />
    </>
);

const InfoRow = ({ label, children }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap="16px" sx={{ py: '8px' }}>
        <Typography sx={{ fontSize: '14px', color: '#727A90' }}>{label}</Typography>
        <Box sx={{ fontSize: '14px', color: '#1D1F2C', fontWeight: 500, textAlign: 'right' }}>{children}</Box>
    </Box>
);

const TotalRow = ({ label, value, bold }) => (
    <Box display="flex" justifyContent="space-between" gap="40px" sx={{ py: '4px' }}>
        <Typography sx={{ fontSize: bold ? '17px' : '14px', fontWeight: bold ? 600 : 400, color: bold ? '#1D1F2C' : '#4D4D4D' }}>
            {label}
        </Typography>
        <Typography sx={{ fontSize: bold ? '17px' : '14px', fontWeight: bold ? 600 : 500, color: '#1D1F2C' }}>{value}</Typography>
    </Box>
);

const thSx = { textAlign: 'left', padding: '12px 16px', fontSize: '13px', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' };
const tdSx = { padding: '14px 16px', fontSize: '14px', color: '#1D1F2C', borderBottom: '1px solid #F0F1F3', verticalAlign: 'top' };

const ReceivedOrderDetails = () => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const { loading, refresh, orderDetails } = useSelector((state) => state.receivedOrders);

    const [anchorEl, setAnchorEl] = useState(null);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [uploadingPo, setUploadingPo] = useState(false);
    const poInputRef = useRef(null);

    useEffect(() => {
        dispatch(getRecievedOrderDetailsThunk(id));
    }, [dispatch, id, refresh]);

    const currentStatus = orderDetails?.status || 'NEW';
    const isClosed = ['CANCELED', 'CANCELLED', 'DELIVERED'].includes(currentStatus);

    const part = orderDetails?.partList || {};
    const { subTotal, gst, grandTotal } = computeGstTotals(part?.productNumber ? [part] : []);

    const address = orderDetails?.shippingAddress || {};
    const purchaseOrder = orderDetails?.vendorPurchaseOrder;
    const addressLines = [
        address.factoryUnits,
        address.streetAddress,
        [address.state, address.zipCode].filter(Boolean).join(' - '),
        address.country,
    ].filter(Boolean);

    const changeStatus = async (status) => {
        if (!status || status === currentStatus) return;
        setUpdating(true);
        try {
            await api.updateRecievedOrderStatus(id, status);
            toast.success(`Order marked as ${STATUS_STYLES[status]?.label || status}`);
            dispatch(setReceiveOrdersRefresh());
        } catch (err) {
            console.log(err);
            toast.error(err?.response?.data?.message || 'Could not update the status. Please try again.');
        } finally {
            setUpdating(false);
            setCancelOpen(false);
        }
    };

    const handleSelectStatus = (status) => {
        setAnchorEl(null);
        changeStatus(status);
    };

    const uploadPurchaseOrder = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        // PDF, Excel or CSV
        if (!/\.(pdf|xlsx|xls|csv)$/i.test(file.name)) {
            toast.error('Please select a PDF, Excel (.xlsx/.xls) or CSV purchase order.');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error('The file must be 10 MB or smaller.');
            return;
        }
        const contentType = file.type || 'application/octet-stream';

        setUploadingPo(true);
        try {
            const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
            const uploadResponse = await api.getPutSignedUrl({
                key: `vendor-purchase-orders/${id}/${Date.now()}_${safeName}`,
                fileType: contentType,
                fileName: file.name,
            });
            const uploadData = uploadResponse?.data?.data;
            if (!uploadData?.preSigned || !uploadData?.url) {
                throw new Error('Upload URL was not returned');
            }

            await axios.put(uploadData.preSigned, file, {
                headers: { 'Content-Type': contentType },
            });
            await api.uploadVendorPurchaseOrder(id, { uri: uploadData.url, fileName: file.name });
            toast.success('Purchase-order file uploaded.');
            dispatch(setReceiveOrdersRefresh());
        } catch (error) {
            console.error(error);
            toast.error(error?.response?.data?.message || 'Could not upload the purchase-order file.');
        } finally {
            setUploadingPo(false);
        }
    };

    if (loading && !orderDetails?._id) {
        return (
            <Myprofile>
                <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 10, width: '100%' }}>
                    <CircularProgress size={32} sx={{ color: '#512DA8' }} />
                </Box>
            </Myprofile>
        );
    }

    return (
        <Myprofile>
            <Box sx={{ borderLeft: '1px solid #DCDEE3', p: { xs: '12px 0', md: '16px 24px 32px' }, width: '100%', minWidth: 0, background: 'linear-gradient(180deg, #FAFAFD 0%, #FFFFFF 46%)' }}>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="20px" sx={{ mb: 3, px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 }, border: '1px solid #E9E3F5', borderRadius: '14px', background: 'linear-gradient(105deg, #FFFFFF 0%, #FCFAFF 58%, #F7F3FF 100%)', boxShadow: '0 8px 24px rgba(81, 45, 168, 0.05)' }}>
                    <Box>
                        <Typography sx={{ color: '#7441D2', fontSize: '11px', fontWeight: 700, letterSpacing: '0.11em', textTransform: 'uppercase', mb: '5px' }}>
                            Order management
                        </Typography>
                        <Box display="flex" alignItems="center" flexWrap="wrap" gap="10px">
                            <Typography sx={{ color: '#1D1F2C', fontSize: { xs: '20px', sm: '24px' }, fontWeight: 700, letterSpacing: '-0.02em' }}>
                                Purchase order #{orderDetails?._id || id}
                            </Typography>
                            <StatusChip status={currentStatus} />
                        </Box>
                        <Typography sx={{ color: '#727A90', fontSize: '14px', mt: '6px' }}>
                            Received on {formatDate(orderDetails?.createdAt)}
                            {orderDetails?.orderType ? ` · ${orderDetails.orderType} order` : ''}
                        </Typography>
                    </Box>

                    <Box display="flex" gap="10px" alignItems="center">
                        <Button
                            variant="outlined"
                            disabled={isClosed || updating}
                            onClick={() => setCancelOpen(true)}
                            sx={{
                                textTransform: 'none', fontWeight: 600, borderRadius: '8px',
                                color: '#F43224', borderColor: '#F43224',
                                '&:hover': { borderColor: '#F43224', background: '#FEECEB' },
                            }}
                        >
                            Cancel order
                        </Button>
                        <Button
                            variant="contained"
                            disabled={['CANCELED', 'CANCELLED'].includes(currentStatus) || updating}
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                            endIcon={updating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <ArrowDropDown />}
                            sx={{
                                textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none',
                                background: '#512DA8', '&:hover': { background: '#4527A0', boxShadow: 'none' },
                            }}
                        >
                            Update status
                        </Button>
                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                            {STATUS_OPTIONS.map((status) => (
                                <MenuItem
                                    key={status}
                                    selected={status === currentStatus}
                                    onClick={() => handleSelectStatus(status)}
                                    sx={{ fontSize: '14px', minWidth: '180px', display: 'flex', justifyContent: 'space-between', gap: '16px' }}
                                >
                                    {STATUS_STYLES[status].label}
                                    {status === currentStatus && <Check fontSize="small" sx={{ color: '#512DA8' }} />}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                </Box>

                {/* Summary + address */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ ...cardSx, height: '100%', overflow: 'hidden' }}>
                            <CardHeader title="Order overview" subtitle="Key order information" icon={ReceiptLongOutlined} />
                            <Box sx={{ p: '8px 20px 12px' }}>
                                <InfoRow label="Order ID">#{orderDetails?._id || id}</InfoRow>
                                <InfoRow label="Order type">{orderDetails?.orderType || '-'}</InfoRow>
                                <InfoRow label="Order date">{formatDate(orderDetails?.createdAt)}</InfoRow>
                                <InfoRow label="Status"><StatusChip status={currentStatus} /></InfoRow>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ ...cardSx, height: '100%', overflow: 'hidden' }}>
                            <CardHeader title="Shipping address" subtitle={addressLines.length ? 'Delivery destination' : 'Shared after confirmation'} icon={LocalShippingOutlined} />
                            <Box sx={{ p: '16px 20px' }}>
                                {addressLines.length ? (
                                    addressLines.map((line) => (
                                        <Typography key={line} sx={{ fontSize: '14px', color: '#1D1F2C', lineHeight: 1.8 }}>{line}</Typography>
                                    ))
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', borderRadius: '9px', p: '11px 12px', background: '#FAFAFC' }}>
                                        <LocalShippingOutlined sx={{ color: '#9AA0B3', fontSize: 20 }} />
                                        <Box>
                                            <Typography sx={{ fontSize: '13px', color: '#343746', fontWeight: 600 }}>Address pending</Typography>
                                            <Typography sx={{ fontSize: '13px', color: '#727A90', mt: '1px' }}>Electrowiz will share it with you shortly.</Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Order tracker */}
                <Paper sx={{ ...cardSx, mt: 3, px: { xs: 1, sm: 3 } }}>
                    <OrderProgress status={currentStatus} type="received" />
                </Paper>

                {/* PO sent by Electrowiz (schedule orders) */}
                {orderDetails?.electrowizPurchaseOrder?.uri && (
                    <Paper sx={{ ...cardSx, mt: 3, overflow: 'hidden' }}>
                        <CardHeader
                            title={`Purchase order from Electrowiz${orderDetails?.poNumber ? ` · ${orderDetails.poNumber}` : ''}`}
                            subtitle="Download the PO Electrowiz sent you for this order."
                            icon={ReceiptLongOutlined}
                        />
                        <Box sx={{ p: { xs: '16px', sm: '18px 20px' }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1D1F2C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                                {orderDetails.electrowizPurchaseOrder.fileName || 'Purchase order'}
                            </Typography>
                            <Button
                                component="a"
                                href={orderDetails.electrowizPurchaseOrder.uri}
                                target="_blank"
                                rel="noreferrer"
                                variant="outlined"
                                endIcon={<OpenInNew fontSize="small" />}
                                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', color: '#512DA8', borderColor: '#512DA8' }}
                            >
                                Open PO
                            </Button>
                        </Box>
                    </Paper>
                )}

                {/* Vendor purchase order */}
                <Paper sx={{ ...cardSx, mt: 3, overflow: 'hidden' }}>
                    <CardHeader
                        title="Your purchase order"
                        subtitle="Upload the PO you issued for this order (PDF, Excel or CSV) so Electrowiz can review it."
                        icon={PictureAsPdfOutlined}
                    />
                    <Box sx={{ p: { xs: '16px', sm: '18px 20px' }, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                            <Box sx={{ width: 38, height: 38, display: 'grid', placeItems: 'center', flex: '0 0 auto', borderRadius: '10px', background: purchaseOrder?.uri ? '#EAF8F1' : '#FAFAFC', color: purchaseOrder?.uri ? '#07875C' : '#727A90' }}>
                                <PictureAsPdfOutlined fontSize="small" />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1D1F2C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {purchaseOrder?.uri ? purchaseOrder.fileName || 'Purchase-order file uploaded' : 'No purchase-order file uploaded'}
                                </Typography>
                                <Typography sx={{ mt: '2px', fontSize: '13px', color: '#727A90' }}>
                                    {purchaseOrder?.uri ? `Uploaded ${formatDate(purchaseOrder.uploadedAt)}` : 'PDF, Excel or CSV · maximum file size 10 MB'}
                                </Typography>
                            </Box>
                        </Box>
                        <Box display="flex" gap="10px" flexWrap="wrap">
                            {purchaseOrder?.uri && (
                                <Button
                                    component="a"
                                    href={purchaseOrder.uri}
                                    target="_blank"
                                    rel="noreferrer"
                                    variant="outlined"
                                    endIcon={<OpenInNew fontSize="small" />}
                                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', color: '#512DA8', borderColor: '#512DA8' }}
                                >
                                    View file
                                </Button>
                            )}
                            <input ref={poInputRef} type="file" accept=".pdf,.xlsx,.xls,.csv" hidden onChange={uploadPurchaseOrder} />
                            <Button
                                variant="contained"
                                onClick={() => poInputRef.current?.click()}
                                disabled={uploadingPo}
                                startIcon={uploadingPo ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <AttachFile fontSize="small" />}
                                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none', background: '#512DA8', '&:hover': { background: '#4527A0', boxShadow: 'none' } }}
                            >
                                {uploadingPo ? 'Uploading…' : purchaseOrder?.uri ? 'Replace file' : 'Upload PO'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Product */}
                <Paper sx={{ ...cardSx, mt: 3, overflow: 'hidden' }}>
                    <CardHeader title="Products" subtitle={part?.productNumber ? '1 line item' : 'No line items'} icon={Inventory2Outlined} />
                    <Box sx={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ background: '#1D1F2C' }}>
                                <tr>
                                    <th style={thSx}>Part number</th>
                                    <th style={thSx}>Quantity</th>
                                    <th style={thSx}>Unit price</th>
                                    <th style={thSx}>MOQ</th>
                                    <th style={{ ...thSx, textAlign: 'right' }}>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {part?.productNumber ? (
                                    <tr>
                                        <td style={tdSx}>
                                            <div style={{ fontWeight: 600 }}>{part.productNumber}</div>
                                            {part.manufacturer && (
                                                <div style={{ color: '#727A90', fontSize: '13px', marginTop: '2px' }}>{part.manufacturer}</div>
                                            )}
                                        </td>
                                        <td style={tdSx}>{formatQty(part.quantity)}</td>
                                        <td style={tdSx}>{Number(part.quotePrice) > 0 ? formatINR(part.quotePrice) : '-'}</td>
                                        <td style={tdSx}>{formatQty(part.moq)}</td>
                                        <td style={{ ...tdSx, textAlign: 'right', fontWeight: 600 }}>{formatINR(subTotal)}</td>
                                    </tr>
                                ) : (
                                    <tr>
                                        <td style={{ ...tdSx, textAlign: 'center', color: '#727A90' }} colSpan={5}>No product details</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </Box>

                    <Box sx={{ p: { xs: '14px', sm: '18px 20px' }, display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #F0F1F3' }}>
                        <Box sx={{ width: '100%', maxWidth: '320px', p: '12px 14px', borderRadius: '10px', background: '#FAF9FE', border: '1px solid #EEE9F8' }}>
                            <TotalRow label="Sub Total" value={formatINR(subTotal)} />
                            <TotalRow label="Tax (18%)" value={formatINR(gst)} />
                            <Box sx={{ borderTop: '1px solid #E6E6E6', mt: '4px', pt: '4px' }}>
                                <TotalRow label="Grand Total" value={formatINR(grandTotal)} bold />
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            </Box>

            <Dialog open={cancelOpen} onClose={() => !updating && setCancelOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>Cancel this order?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '14px', color: '#4D4D4D' }}>
                        Purchase order #{orderDetails?._id || id} will be marked as cancelled and Electrowiz will be notified.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: '0 24px 20px' }}>
                    <Button onClick={() => setCancelOpen(false)} disabled={updating} sx={{ textTransform: 'none', color: '#4D4D4D' }}>
                        Keep order
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => changeStatus('CANCELED')}
                        disabled={updating}
                        sx={{ textTransform: 'none', boxShadow: 'none', background: '#F43224', '&:hover': { background: '#D92D20', boxShadow: 'none' } }}
                    >
                        {updating ? 'Cancelling...' : 'Cancel order'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Myprofile>
    );
};

export default ReceivedOrderDetails;
